(() => {
  if (window.__gpProductSwatchesInitialized) return;
  window.__gpProductSwatchesInitialized = true;

  const script = document.currentScript;
  const selectYourLabel = script?.dataset.gpSelectYourLabel || 'Select Your:';
  const pickerSelector = 'variant-picker.variant-picker--gp-options';
  const swatchSelector = `${pickerSelector} .variant-option--gp-swatches`;
  const tiers = ['xl', 'large', 'small'];
  const observed = new WeakSet();
  const initializedPickers = new WeakSet();
  const changedPickers = new Set();
  const lastWidths = new WeakMap();
  const scheduled = new WeakSet();

  document.documentElement.classList.add('gp-variant-selection-labels');

  const pickerKey = (picker) => `${picker.dataset.sectionId || ''}:${picker.dataset.blockId || ''}`;

  const hasExplicitVariantSelection = () => new URL(window.location.href).searchParams.has('variant');

  const syncHistorySelectionState = (picker) => {
    const key = pickerKey(picker);
    if (hasExplicitVariantSelection()) changedPickers.add(key);
    else changedPickers.delete(key);
  };

  const uniqueValues = (values) => [...new Set(values.filter(Boolean))];

  const optionNames = (picker) =>
    uniqueValues(
      [...picker.querySelectorAll('[data-option-name]')].map((control) => control.dataset.optionName?.trim())
    );

  const selectedValues = (picker) =>
    [
      ...picker.querySelectorAll('input[data-option-name]:checked'),
      ...picker.querySelectorAll('select option[data-option-name]:checked'),
    ]
      .map((control) => control.value.trim())
      .filter(Boolean);

  const selectedCustomColorSwatch = (picker) => {
    const colorOption = picker.querySelector("fieldset.variant-option[data-option-role='color']");
    const selectedControl = colorOption?.querySelector('input[data-option-name]:checked');

    return selectedControl
      ?.closest('.variant-option__button-label')
      ?.querySelector('.swatch.swatch--gp-custom');
  };

  const isMainPdpPicker = (picker) =>
    picker.hasAttribute('data-template-product-match') &&
    /(^|\/)products\/[^/]+/.test(window.location.pathname) &&
    !picker.closest('quick-add-dialog');

  const customSplitColors = (swatch) => {
    const background = swatch.style.getPropertyValue('--swatch-background').trim();
    if (!background.startsWith('linear-gradient(')) return [];
    return background.match(/#[0-9a-f]{3,8}\b/gi) ?? [];
  };

  const decorateLegacySplitSwatch = (swatch) => {
    const colors = customSplitColors(swatch);
    const existing = swatch.querySelector(':scope > .gp-legacy-swatch-circle');

    if (colors.length < 2) {
      existing?.remove();
      swatch.classList.remove('gp-legacy-split-swatch');
      swatch.style.removeProperty('--gp-legacy-swatch-primary');
      swatch.style.removeProperty('--gp-legacy-swatch-secondary');
      return;
    }

    let circle = existing;
    if (!circle) {
      circle = document.createElement('span');
      circle.className = 'gp-legacy-swatch-circle';
      circle.setAttribute('aria-hidden', 'true');

      const secondary = document.createElement('span');
      secondary.className = 'gp-legacy-swatch-half gp-legacy-swatch-half--secondary';
      const primary = document.createElement('span');
      primary.className = 'gp-legacy-swatch-half gp-legacy-swatch-half--primary';
      circle.append(secondary, primary);
      swatch.prepend(circle);
    }

    swatch.classList.add('gp-legacy-split-swatch');
    swatch.style.setProperty('--gp-legacy-swatch-primary', colors[0]);
    swatch.style.setProperty('--gp-legacy-swatch-secondary', colors[1]);
  };

  const refineMainPdpPicker = (picker) => {
    const shouldRefine = isMainPdpPicker(picker);
    picker.classList.toggle('variant-picker--gp-main-pdp', shouldRefine);
    if (!shouldRefine) return;

    picker.querySelectorAll('.swatch.swatch--gp-custom').forEach(decorateLegacySplitSwatch);
  };

  const nameOptionGroups = (picker) => {
    for (const fieldset of picker.querySelectorAll('fieldset.variant-option')) {
      const name = fieldset.querySelector('[data-option-name]')?.dataset.optionName?.trim();
      if (name && fieldset.getAttribute('aria-label') !== name) fieldset.setAttribute('aria-label', name);
    }

    for (const select of picker.querySelectorAll('select')) {
      const name = select.querySelector('option[data-option-name]')?.dataset.optionName?.trim();
      if (name && select.getAttribute('aria-label') !== name) select.setAttribute('aria-label', name);
    }
  };

  const selectionLabel = (picker) => {
    const form = picker.querySelector('.variant-picker__form');
    if (!form) return null;

    let label = form.querySelector(':scope > .gp-variant-selection-label');
    if (label) return label;

    label = document.createElement('div');
    label.className = 'gp-variant-selection-label';
    label.setAttribute('aria-live', 'polite');
    form.prepend(label);
    return label;
  };

  const updateSelectionPresentation = (picker) => {
    if (!(picker instanceof HTMLElement) || !picker.isConnected) return;

    nameOptionGroups(picker);

    const hasChanged = changedPickers.has(pickerKey(picker));
    const values = hasChanged ? selectedValues(picker) : optionNames(picker);
    const text = `${hasChanged ? '' : `${selectYourLabel} `}${values.join(' / ')}`.trim();
    const label = selectionLabel(picker);

    if (label && label.textContent !== text) label.textContent = text;

    if (!hasChanged || values.length === 0) return;

    const mobileFlavor = picker
      .closest('.product-information')
      ?.querySelector('.gruff-product-summary__mobile-flavor');

    if (mobileFlavor) {
      const summaryText = values.join(' / ');
      const customColorSwatch = selectedCustomColorSwatch(picker);

      mobileFlavor.textContent = '';
      mobileFlavor.classList.toggle(
        'gruff-product-summary__mobile-flavor--with-swatch',
        Boolean(customColorSwatch)
      );

      if (customColorSwatch) {
        const miniSwatch = customColorSwatch.cloneNode(true);
        miniSwatch.classList.add('gruff-product-summary__mobile-swatch');
        miniSwatch.setAttribute('aria-hidden', 'true');
        mobileFlavor.append(miniSwatch, document.createTextNode(summaryText));
      } else {
        mobileFlavor.textContent = summaryText;
      }

      mobileFlavor.setAttribute('aria-live', 'polite');
    }
  };

  const initializePicker = (picker) => {
    if (!(picker instanceof HTMLElement)) return;

    refineMainPdpPicker(picker);
    if (!initializedPickers.has(picker)) syncHistorySelectionState(picker);
    updateSelectionPresentation(picker);
    if (initializedPickers.has(picker)) return;

    initializedPickers.add(picker);
    picker.addEventListener('change', (event) => {
      const control = event.target;
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;
      if (control instanceof HTMLInputElement && !control.dataset.optionName) return;
      if (control instanceof HTMLSelectElement && !control.querySelector('option[data-option-name]')) return;

      changedPickers.add(pickerKey(picker));
      updateSelectionPresentation(picker);
      requestAnimationFrame(() => updateSelectionPresentation(picker));
    });
  };

  const rowCount = (fieldset) => {
    const labels = [...fieldset.children].filter(
      (child) => child instanceof HTMLElement && child.classList.contains('variant-option__button-label--has-swatch')
    );

    const rows = [];
    for (const label of labels) {
      if (label.offsetParent === null) continue;
      const top = label.getBoundingClientRect().top;
      if (!rows.some((rowTop) => Math.abs(rowTop - top) < 2)) rows.push(top);
    }

    return rows.length;
  };

  const fit = (fieldset) => {
    scheduled.delete(fieldset);
    if (!(fieldset instanceof HTMLElement) || !fieldset.isConnected || fieldset.clientWidth === 0) return;

    for (const tier of tiers) {
      fieldset.dataset.gpSwatchSize = tier;
      if (rowCount(fieldset) <= 2 || tier === tiers.at(-1)) break;
    }
  };

  const scheduleFit = (fieldset) => {
    if (scheduled.has(fieldset)) return;
    scheduled.add(fieldset);
    requestAnimationFrame(() => fit(fieldset));
  };

  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const width = Math.round(entry.contentRect.width);
      if (lastWidths.get(entry.target) === width) continue;
      lastWidths.set(entry.target, width);
      scheduleFit(entry.target);
    }
  });

  const observe = (fieldset) => {
    if (!(fieldset instanceof HTMLElement) || observed.has(fieldset)) return;
    observed.add(fieldset);
    resizeObserver.observe(fieldset);
    scheduleFit(fieldset);
  };

  const refreshHistoryPresentation = () => {
    document.querySelectorAll(pickerSelector).forEach((picker) => {
      syncHistorySelectionState(picker);
      updateSelectionPresentation(picker);
      requestAnimationFrame(() => updateSelectionPresentation(picker));
    });
  };

  const setup = (root = document) => {
    if (root instanceof Element) {
      if (root.matches(swatchSelector)) observe(root);
      if (root.matches(pickerSelector)) initializePicker(root);

      const picker = root.closest(pickerSelector);
      if (picker) initializePicker(picker);
    }

    root.querySelectorAll?.(swatchSelector).forEach(observe);
    root.querySelectorAll?.(pickerSelector).forEach(initializePicker);
  };

  setup();

  window.addEventListener('popstate', () => requestAnimationFrame(refreshHistoryPresentation));
  window.addEventListener('pageshow', () => requestAnimationFrame(refreshHistoryPresentation));
  document.addEventListener('shopify:section:load', (event) => setup(event.target));

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        const fieldset = node.closest(swatchSelector);
        if (fieldset) observe(fieldset);
        setup(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
